/**
 * Clara 360 - Template Management Service
 * Manages user-editable communication templates
 */

import { masterSupabase as supabase } from '../lib/supabase-master.js';

/**
 * Get all templates for a channel and dunning level
 */
async function getTemplates(channel, dunningLevel = null) {
  try {
    let query = supabase
      .from('communication_templates')
      .select('*')
      .eq('channel', channel)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });
    
    if (dunningLevel !== null) {
      query = query.eq('dunning_level', dunningLevel);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Error loading templates:', error);
    throw error;
  }
}

/**
 * Get single template by ID
 */
async function getTemplate(id) {
  try {
    const { data, error } = await supabase
      .from('communication_templates')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error loading template:', error);
    throw error;
  }
}

/**
 * Create new template
 */
async function createTemplate(templateData) {
  try {
    const { data, error } = await supabase
      .from('communication_templates')
      .insert({
        name: templateData.name,
        channel: templateData.channel,
        dunning_level: templateData.dunning_level,
        message_template: templateData.message_template,
        subject_template: templateData.subject_template,
        body_template: templateData.body_template,
        description: templateData.description,
        is_default: false,
        is_active: true
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error creating template:', error);
    throw error;
  }
}

/**
 * Update existing template
 */
async function updateTemplate(id, templateData) {
  try {
    const { data, error } = await supabase
      .from('communication_templates')
      .update({
        name: templateData.name,
        message_template: templateData.message_template,
        subject_template: templateData.subject_template,
        body_template: templateData.body_template,
        description: templateData.description,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('is_default', false) // Can't update default templates
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error updating template:', error);
    throw error;
  }
}

/**
 * Delete template
 */
async function deleteTemplate(id) {
  try {
    const { error } = await supabase
      .from('communication_templates')
      .delete()
      .eq('id', id)
      .eq('is_default', false); // Can't delete default templates
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('❌ Error deleting template:', error);
    throw error;
  }
}

/**
 * Increment usage count
 */
async function incrementUsage(id) {
  try {
    const { error } = await supabase.rpc('increment_template_usage', { template_id: id });
    if (error) throw error;
  } catch (error) {
    // Non-critical, just log
    console.warn('⚠️ Could not increment usage:', error);
  }
}

/**
 * Copy default template to user template
 */
async function copyTemplate(templateId, newName) {
  try {
    // Get original template
    const original = await getTemplate(templateId);
    
    // Create copy
    return await createTemplate({
      name: newName || `${original.name} (Kopie)`,
      channel: original.channel,
      dunning_level: original.dunning_level,
      message_template: original.message_template,
      subject_template: original.subject_template,
      body_template: original.body_template,
      description: original.description
    });
  } catch (error) {
    console.error('❌ Error copying template:', error);
    throw error;
  }
}

export default {
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  incrementUsage,
  copyTemplate
};
